// src/App.js - FIXED VERSION
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import './App.css';

import { Analytics } from "@vercel/analytics/react"

// Auth Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Firebase services
import { getTestById } from './firebase/testService';

const TestTaking = React.lazy(() => import('./components/TestTaking'));
const TestResult = React.lazy(() => import('./components/TestResult'));
const CertificateVerification = React.lazy(() => import('./components/CertificateVerification'));
const CollegeTestManagement = React.lazy(() => import('./components/CollegeTestManagement'));
const StudentProfile = React.lazy(() => import('./components/StudentProfile'));

// Lazy load components
const PublicDashboard = React.lazy(() => import('./components/PublicDashboard'));
const StudentDashboard = React.lazy(() => import('./components/StudentDashboard'));
const CollegeDashboard = React.lazy(() => import('./components/CollegeDashboard'));
const AuthPage = React.lazy(() => import('./components/AuthPage'));
const TestDetails = React.lazy(() => import('./components/TestDetails'));

// Loading Component
const LoadingScreen = () => (
  <div style={{ 
    minHeight: '100vh', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    background: '#f9fafb'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '50px',
        height: '50px',
        border: '4px solid #e5e7eb',
        borderTop: '4px solid #2563eb',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 1rem'
      }}></div>
      <p style={{ color: '#6b7280' }}>Loading...</p>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, userData, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (allowedRoles && userData && !allowedRoles.includes(userData.role)) {
    if (userData.role === 'student') {
      return <Navigate to="/student/dashboard" replace />;
    } else if (userData.role === 'college') {
      return <Navigate to="/college/dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};

// Home Component
const Home = () => {
  const { isAuthenticated, userData, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && isAuthenticated && userData) {
      if (userData.role === 'student') {
        navigate('/student/dashboard', { replace: true });
      } else if (userData.role === 'college') {
        navigate('/college/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, userData, loading, navigate]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <React.Suspense fallback={<LoadingScreen />}>
      <PublicDashboard 
        onTestSelect={(test) => navigate(`/test/${test.id}`)}
        onAuthRedirect={() => navigate('/auth')}
      />
    </React.Suspense>
  );
};

// Test Details Page with real data
const TestDetailsPage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTest();
  }, [testId]);

  const loadTest = async () => {
    setLoading(true);
    try {
      const result = await getTestById(testId);
      if (result.success) {
        setTest(result.test);
      } else {
        setError('Test not found');
      }
    } catch (err) {
      setError('Error loading test');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error || !test) {
    return (
      <div className="app">
        <div className="container" style={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <div className="card" style={{ textAlign: 'center' }}>
            <h2 className="h2">Test Not Found</h2>
            <p className="text-gray-600 mb-4">The test you're looking for doesn't exist or has been removed.</p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/')}
            >
              ← Back to Tests
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <React.Suspense fallback={<LoadingScreen />}>
      <TestDetails
        test={test}
        onBack={() => navigate('/')}
        onStartTest={() => navigate(`/auth?testId=${testId}`)}
      />
    </React.Suspense>
  );
};

// Auth Page Wrapper
const AuthPageWrapper = () => {
  const { isAuthenticated, userData, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && isAuthenticated && userData) {
      if (userData.role === 'student') {
        navigate('/student/dashboard', { replace: true });
      } else if (userData.role === 'college') {
        navigate('/college/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, userData, loading, navigate]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return null;
  }

  return (
    <React.Suspense fallback={<LoadingScreen />}>
      <AuthPage 
        selectedTest={null}
        onBack={() => navigate('/')}
        onAuthSuccess={() => {}}
      />
    </React.Suspense>
  );
};

// Main App Content
function AppContent() {
  return (
    <div className="App">
      <React.Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/auth" element={<AuthPageWrapper />} />
          <Route path="/test/:testId" element={<TestDetailsPage />} />
          
          {/* Student Routes */}
          <Route 
            path="/student/dashboard" 
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentDashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/take-test/:testId" 
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <React.Suspense fallback={<LoadingScreen />}>
                  <TestTaking />
                </React.Suspense>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/test-result/:attemptId" 
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <React.Suspense fallback={<LoadingScreen />}>
                  <TestResult />
                </React.Suspense>
              </ProtectedRoute>
            } 
          />
          
          {/* College Routes */}
          <Route 
            path="/college/dashboard" 
            element={
              <ProtectedRoute allowedRoles={['college']}>
                <CollegeDashboard />
              </ProtectedRoute>
            } 
          />

          {/* College Test Management - MOVED INSIDE ROUTES */}
          <Route 
            path="/college/test/:testId" 
            element={
              <ProtectedRoute allowedRoles={['college']}>
                <React.Suspense fallback={<LoadingScreen />}>
                  <CollegeTestManagement />
                </React.Suspense>
              </ProtectedRoute>
            } 
          />

          {/* Student Profile - MOVED INSIDE ROUTES */}
          <Route 
            path="/student-profile/:studentId" 
            element={
              <ProtectedRoute allowedRoles={['college', 'admin']}>
                <React.Suspense fallback={<LoadingScreen />}>
                  <StudentProfile />
                </React.Suspense>
              </ProtectedRoute>
            } 
          />

          {/* Certificate Verification - Public */}
          <Route 
            path="/verify/:attemptId" 
            element={
              <React.Suspense fallback={<LoadingScreen />}>
                <CertificateVerification />
              </React.Suspense>
            } 
          />
          
          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </React.Suspense>
    </div>
  );
}

// Main App
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
        <Analytics />
      </AuthProvider>
    </Router>
  );
}

export default App;